/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import {InputAdornment} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {MessageEndpoint, MessageEndpointTypes} from '@ion/core/src/types/entities';
import {isValidEmail} from '@ion/core/src/utils/Validators';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import FormItem from '@ion/dls/src/components/forms/FormItem';
import FormSecureTextField from '@ion/dls/src/components/forms/FormSecureTextField';
import FormTextField from '@ion/dls/src/components/forms/FormTextField';
import {FormState} from '@ion/dls/src/hooks/useFormState';
import {
  getInitialGCPPubSubFormState,
  getIsGCPPubSubType,
  MessageEndpointFormFields,
  sanitizePrivateKeyValue
} from './MessageEndpointUtils';

export interface GCPPubSubMessageEndpointProps {
  messageEndpoint: MessageEndpoint;
  formState: FormState;
  setFormState: (state: FormState | null, reset?: boolean) => void;
  type: MessageEndpointTypes;
  isView?: boolean;
  isAdd?: boolean;
  isEdit?: boolean;
  isLoggedinUserAccount?: boolean;
  onFieldChange: (e: ChangeEvent) => void;
}

interface GCPPubSubFieldConfig {
  name: MessageEndpointFormFields;
  translationKey:
  | 'topicName'
  | 'projectId'
  | 'clientEmail'
  | 'clientId'
  | 'privateKeyId'
  | 'certUrl'
  | 'privateKey';
  required?: boolean;
  secure?: boolean;
}

const gcpPubSubFields: GCPPubSubFieldConfig[] = [
  {name: MessageEndpointFormFields.GCP_TOPIC_NAME, translationKey: 'topicName', required: true},
  {name: MessageEndpointFormFields.PROJECT_ID, translationKey: 'projectId', required: true},
  {name: MessageEndpointFormFields.CLIENT_EMAIL, translationKey: 'clientEmail', required: true},
  {name: MessageEndpointFormFields.CLIENT_ID, translationKey: 'clientId', required: true},
  {name: MessageEndpointFormFields.PRIVATE_KEY_ID, translationKey: 'privateKeyId', required: true},
  {name: MessageEndpointFormFields.CERT_URL, translationKey: 'certUrl'},
  {name: MessageEndpointFormFields.PRIVATE_KEY, translationKey: 'privateKey', required: true, secure: true}
];

interface ChangeAdornmentProps {
  onChangeField: (name: string) => void;
  name: string;
}

const ChangeAdornment = ({onChangeField, name}: ChangeAdornmentProps) => {
  const onClick = useCallback(() => onChangeField(name), [name, onChangeField]);
  return (
    <InputAdornment position='end'>
      <TertiaryButton onClick={onClick}>
        {translate('common:change')}
      </TertiaryButton>
    </InputAdornment>
  );
};

/**
 * Displays a Google Pub Sub style account message endpoint form & handles all related form functionality.
 *
 * This is strictly to be used in the context of a @see MessageEndpoint component.
 *
 * ```typescript
 *
 * import GCPPubSubMessageEndpoint from './GCPPubSubMessageEndpoint';
 *
 * <GCPPubSubMessageEndpoint isView={isView}
 *   isLoggedinUserAccount={isLoggedinUserAccount}
 *   formState={formState}
 *   setFormState={setFormState}
 *   messageEndpoint={messageEndpoint}
 *   type={messageType}
 *   onFieldChange={onFieldChange} />
 *
 * ```
 */
const GCPPubSubMessageEndpoint = ({
  type,
  formState,
  setFormState,
  onFieldChange,
  messageEndpoint,
  isAdd,
  isEdit,
  isView = false,
  isLoggedinUserAccount = false
}: GCPPubSubMessageEndpointProps): JSX.Element => {
  const [originalPrivateKey, setOriginalPrivateKey]     = useState<string | undefined>();
  const [originalClientId, setOriginalClientId]         = useState<string | undefined>();
  const [originalPrivateKeyId, setOriginalPrivateKeyId] = useState<string | undefined>();
  const [changedFields, setChangedFields]               = useState<Set<string>>(new Set());

  const onChangeField = useCallback((fieldName: string) => {
    setChangedFields((prev) => new Set([...prev, fieldName]));
  }, []);
  
  const fields     = translate('admin-groups:dialog.messageEndpoint.fields', {returnObjects: true}) || {};
  const isDisabled = Boolean(isView || isLoggedinUserAccount);

  useEffect((): void => {
    if (getIsGCPPubSubType(type)) {
      if (messageEndpoint?.configuration) {
        setOriginalPrivateKey(messageEndpoint.configuration?.[MessageEndpointFormFields.PRIVATE_KEY]);
        setOriginalClientId(messageEndpoint.configuration?.[MessageEndpointFormFields.CLIENT_ID]);
        setOriginalPrivateKeyId(messageEndpoint.configuration?.[MessageEndpointFormFields.PRIVATE_KEY_ID]);
      }
      setFormState(getInitialGCPPubSubFormState(type, messageEndpoint?.configuration));
    } else {
      setFormState(null, true);
    }
  }, [messageEndpoint, type]);

  const privateKeyValue   = formState[MessageEndpointFormFields.PRIVATE_KEY]?.value;
  const clientIdValue     = formState[MessageEndpointFormFields.CLIENT_ID]?.value;
  const privateKeyIdValue = formState[MessageEndpointFormFields.PRIVATE_KEY_ID]?.value;

  useEffect((): void => {
    const anyFilled = Boolean(privateKeyValue || clientIdValue || privateKeyIdValue);
    setFormState({
      [MessageEndpointFormFields.PRIVATE_KEY] : {
        ...formState[MessageEndpointFormFields.PRIVATE_KEY],
        required : anyFilled
      },
      [MessageEndpointFormFields.CLIENT_ID] : {
        ...formState[MessageEndpointFormFields.CLIENT_ID],
        required : anyFilled
      },
      [MessageEndpointFormFields.PRIVATE_KEY_ID] : {
        ...formState[MessageEndpointFormFields.PRIVATE_KEY_ID],
        required : anyFilled
      }
    });
  }, [isEdit, privateKeyValue, clientIdValue, privateKeyIdValue]);

  const onClientEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    setFormState({
      [name] : {
        name,
        required : true,
        value,
        error    : value ? !isValidEmail(value) : false
      }
    });
  }, [isValidEmail]);

  const onPrivateKeyChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const {name, value, required = false} = e.target;
    setFormState({
      [name] : {
        name,
        required,
        value : sanitizePrivateKeyValue(value)
      }
    });
  }, []);

  return (
    <>
      {gcpPubSubFields.map(({
        name,
        translationKey,
        required = false,
        secure = false
      }) => {
        const fieldConfig    = fields?.[translationKey] || {};
        const FieldComponent  = secure ? FormSecureTextField : FormTextField;
        const isClientEmail   = name === MessageEndpointFormFields.CLIENT_EMAIL;
        const isPrivateKey    = name === MessageEndpointFormFields.PRIVATE_KEY;
        const isClientId      = name === MessageEndpointFormFields.CLIENT_ID;
        const isPrivateKeyId  = name === MessageEndpointFormFields.PRIVATE_KEY_ID;
        const isSecureField   = isPrivateKey || isClientId || isPrivateKeyId;
        const fieldValue      = formState[name]?.value;
        const onChange        = isClientEmail ? onClientEmailChange
          : isPrivateKey ? onPrivateKeyChange : onFieldChange;

        const isAnySecureFieldFilled = isEdit && Boolean(
          privateKeyValue || clientIdValue || privateKeyIdValue
        );
        const isSecureFieldRequired  = isSecureField && isEdit && isAnySecureFieldFilled;

        const shouldHideError = isSecureField && isEdit && !isAnySecureFieldFilled;
        const shouldShowInvalidEmailError = isClientEmail && fieldValue;

        let errorText;

        if (shouldHideError) {
          errorText = undefined;
        } else if (shouldShowInvalidEmailError) {
          errorText = fieldConfig.invalidEmailMessage;
        } else {
          errorText = fieldConfig.errorMessage;
        }

        let fallbackValue = '';

        if (isPrivateKey) {
          fallbackValue = originalPrivateKey ?? '';
        } else if (isClientId) {
          fallbackValue = originalClientId ?? '';
        } else if (isPrivateKeyId) {
          fallbackValue = originalPrivateKeyId ?? '';
        }

        const isFieldLocked    = isSecureField && isEdit && !changedFields.has(name);
        const EditFieldComponent = isSecureField && isEdit ? FormSecureTextField : FieldComponent;

        const secureProps = isSecureField && (isAdd || isEdit) ? {
          fallbackValue,
          hideCharacters   : true,
          showToggleButton : isEdit ? !isFieldLocked : false
        } : {};

        const changeAdornment = isFieldLocked ? {
          InputProps : {
            endAdornment : <ChangeAdornment onChangeField={onChangeField} name={name} />
          }
        } : {};

        return isSecureField && isView ? null : (
          <FormItem key={name}>
            <EditFieldComponent required={isSecureField ? (!!isAdd || isSecureFieldRequired) : required}
              fieldLabel={fieldConfig.label}
              placeholder={fieldConfig.placeholder}
              errorText={errorText}
              hasError={isSecureField && isEdit && !isAnySecureFieldFilled ? false : formState[name]?.error}
              value={fieldValue}
              name={name}
              onChange={onChange}
              disabled={isDisabled}
              readOnlyText={isFieldLocked}
              {...secureProps}
              {...changeAdornment} />
          </FormItem>
        );
      })}
    </>
  );
};

export default GCPPubSubMessageEndpoint;
